<script setup>
  import { computed, ref, toRaw, watch } from "vue";
  import { useElementSize, useEventListener, useTimeoutFn } from "@vueuse/core";
  import { Loader } from "lucide-vue-next";

  const props = defineProps({
    documentUrl: { type: String, required: true },
    placement: { type: String, default: "feed" },
    variables: { type: Object, required: true },
    template: { type: Object, default: null },
    refreshDocumentUrl: { type: Function, required: true },
  });

  const SIZES = {
    feed: { width: 1080, height: 1080 },
    story: { width: 1080, height: 1920 },
  };
  const RENDER_TIMEOUT_MS = 10_000;

  const container = ref(null);
  const frame = ref(null);
  const rendered = ref(false);
  const error = ref(null);
  const refreshedDocumentUrl = ref(false);

  const { width: boxSize } = useElementSize(container);
  const size = computed(() => SIZES[props.placement] || SIZES.feed);
  const src = computed(() => {
    const url = new URL(props.documentUrl, window.location.origin);
    url.searchParams.set("placement", props.placement);
    return `${url.pathname}${url.search}`;
  });
  const scale = computed(() => boxSize.value / Math.max(size.value.width, size.value.height));

  const handleRenderTimeout = async () => {
    if (!refreshedDocumentUrl.value) {
      refreshedDocumentUrl.value = true;
      const previousSrc = src.value;
      await props.refreshDocumentUrl();
      if (src.value === previousSrc) startRenderTimeout();
      return;
    }
    rendered.value = true;
    error.value = "the template document did not load";
  };

  const { start: startRenderTimeout, stop: stopRenderTimeout } = useTimeoutFn(handleRenderTimeout, RENDER_TIMEOUT_MS, {
    immediate: false,
  });

  const post = (message) => frame.value?.contentWindow?.postMessage(message, "*");

  const postTemplate = () => {
    if (props.template) post({ type: "SET_TEMPLATE", html: props.template.html, css: props.template.css });
  };

  const postVariables = () => post({ type: "SET_VARIABLES", variables: { ...toRaw(props.variables) } });

  const resetRenderState = () => {
    rendered.value = false;
    error.value = null;
    startRenderTimeout();
  };

  const handleLoad = () => {
    resetRenderState();
    postTemplate();
    postVariables();
  };

  useEventListener(window, "message", (event) => {
    if (event.source !== frame.value?.contentWindow) return;
    if (event.data?.type === "CREATIVE_RENDERED") {
      stopRenderTimeout();
      rendered.value = true;
      error.value = null;
      refreshedDocumentUrl.value = false;
    } else if (event.data?.type === "CREATIVE_ERROR") {
      stopRenderTimeout();
      rendered.value = true;
      error.value = event.data.error;
    }
  });

  watch(src, resetRenderState, { immediate: true });
  watch(() => props.variables, postVariables, { deep: true });
  watch(() => props.template, postTemplate, { deep: true });
</script>

<template>
  <div
    ref="container"
    class="relative flex w-full items-center justify-center overflow-hidden bg-muted aspect-square">
    <div
      class="relative overflow-hidden"
      :style="{ width: `${size.width * scale}px`, height: `${size.height * scale}px` }">
      <iframe
        ref="frame"
        :key="src"
        :src="src"
        sandbox="allow-scripts"
        title="Creative live preview"
        class="absolute left-0 top-0 origin-top-left border-0"
        :style="{ width: `${size.width}px`, height: `${size.height}px`, transform: `scale(${scale})` }"
        @load="handleLoad" />
    </div>
    <Loader
      v-if="!rendered"
      :stroke-width="2.5"
      class="size-7 absolute animate-spin text-primary" />
    <p
      v-if="error"
      class="absolute inset-x-2 bottom-2 rounded-md border border-status-error-border bg-status-error-soft p-2 text-xs text-status-error-strong">
      Preview unavailable: {{ error }}
    </p>
  </div>
</template>
